import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, subDays } from "date-fns";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MetricTrend from "../../metrics/metric-trend";
import MetricPartition from "../../metrics/metric-partition";
import MetricValue from "../../metrics/metric-value";
import { useApiTenant } from "../../../../api/api-tenant";
import { getTrendLabels, getLabels } from "../../../../helpers/metrics";
import { metricColors } from "../../../../helpers/colors";
export default function AudienceMetrics({ audience_id, refreshMetrics }) {
    const { t } = useTranslation();
    const api = useApiTenant();
    const [audienceValue, setAudienceValue] = useState(0);
    const [audienceValueCachedAt, setAudienceValueCachedAt] = useState("");
    const [audienceValueRange, setAudienceValuesRange] = useState("total");
    const [audienceValueLoading, setAudienceValueLoading] = useState(true);
    const [audienceValueError, setAudienceValueError] = useState(false);
    const [audienceValueStartDate, setAudienceValueStartDate] = useState("");
    const [audienceValueEndDate, setAudienceValueEndDate] = useState("");
    const [audienceValueStartTime, setAudienceValueStartTime] = useState("");
    const [audienceValueEndTime, setAudienceValueEndTime] = useState("");
    const [isChosenValueCustomRange, setIsChosenValueCustomRange] = useState(false);
    const [audienceTrendLabels, setAudienceTrendLabels] = useState([]);
    const [audienceTrendValues, setAudienceTrendValues] = useState([]);
    const [audienceTrendCachedAt, setAudienceTrendCachedAt] = useState("");
    const [audienceTrendRange, setAudienceTrendRange] = useState("last_7_days");
    const [audienceTrendLoading, setAudienceTrendLoading] = useState(true);
    const [audienceTrendError, setAudienceTrendError] = useState(false);
    const [audienceStatusData, setAudienceStatusData] = useState([]);
    const [audienceStatusCachedAt, setAudienceStatusCachedAt] = useState("");
    const [audienceStatusRange, setAudienceStatusRange] = useState("total");
    const [audienceStatusLoading, setAudienceStatusLoading] = useState(true);
    const [audienceStatusError, setAudienceStatusError] = useState(false);
    const [audienceStatusStartDate, setAudienceStatusStartDate] = useState("");
    const [audienceStatusEndDate, setAudienceStatusEndDate] = useState("");
    const [audienceStatusStartTime, setAudienceStatusStartTime] = useState("");
    const [audienceStatusEndTime, setAudienceStatusEndTime] = useState("");
    const [isChosenStatusCustomRange, setIsChosenStatusCustomRange] = useState(false);
    const [audienceValueMetricsRefresh, setAudienceValueMetricsRefresh] = useState(false);
    const [audienceTrendMetricsRefresh, setAudienceTrendMetricsRefresh] = useState(false);
    const [audienceStatusMetricsRefresh, setAudienceStatusMetricsRefresh] = useState(false);
    const getAudienceValue = invalidateCache => {
        setAudienceValueLoading(true);
        if (audienceValueError)
            setAudienceValueError(false);
        let startTime;
        let endTime;
        if (audienceValueRange === "custom" &&
            audienceValueStartDate !== "" &&
            audienceValueEndDate !== "") {
            if (audienceValueStartTime !== "") {
                startTime =
                    format(new Date(audienceValueStartDate), "yyyy-MM-dd") +
                        " " +
                        format(new Date(audienceValueStartTime), "HH:mm:ss");
            }
            else {
                startTime = format(new Date(audienceValueStartDate), "yyyy-MM-dd") + " 00:00:00";
            }
            if (audienceValueEndTime !== "") {
                endTime =
                    format(new Date(audienceValueEndDate), "yyyy-MM-dd") +
                        " " +
                        format(new Date(audienceValueEndTime), "HH:mm:ss");
            }
            else {
                endTime = format(new Date(audienceValueEndDate), "yyyy-MM-dd") + " 23:59:59";
            }
        }
        api.GET_metrics("count", "leads", audienceValueRange, undefined, undefined, "audience_id", audience_id, undefined, invalidateCache ? 1 : undefined, audienceValueRange === "custom" ? startTime : undefined, audienceValueRange === "custom" ? endTime : undefined)
            .then(response => {
            setAudienceValue(response.data.count);
            setAudienceValueCachedAt(response.data.cached_at);
            setAudienceValueLoading(false);
        })
            .catch(error => {
            setAudienceValueError(true);
            if (!api.isCancel(error)) {
                console.error(error);
                setAudienceValueLoading(false);
            }
        });
        return () => api.cancel();
    };
    const getAudienceStatusValues = invalidateCache => {
        setAudienceStatusLoading(true);
        if (audienceStatusError)
            setAudienceStatusError(false);
        let startTime;
        let endTime;
        if (audienceStatusRange === "custom" &&
            audienceStatusStartDate !== "" &&
            audienceStatusEndDate !== "") {
            if (audienceStatusStartTime !== "") {
                startTime =
                    format(new Date(audienceStatusStartDate), "yyyy-MM-dd") +
                        " " +
                        format(new Date(audienceStatusStartTime), "HH:mm:ss");
            }
            else {
                startTime = format(new Date(audienceStatusStartDate), "yyyy-MM-dd") + " 00:00:00";
            }
            if (audienceStatusEndTime !== "") {
                endTime =
                    format(new Date(audienceStatusEndDate), "yyyy-MM-dd") +
                        " " +
                        format(new Date(audienceStatusEndTime), "HH:mm:ss");
            }
            else {
                endTime = format(new Date(audienceStatusEndDate), "yyyy-MM-dd") + " 23:59:59";
            }
        }
        api.GET_metrics("partition", "leads", audienceStatusRange, "status", undefined, "audience_id", audience_id, undefined, invalidateCache ? 1 : undefined, audienceStatusRange === "custom" ? startTime : undefined, audienceStatusRange === "custom" ? endTime : undefined, "updated_at")
            .then(response => {
            let metricsData = [];
            response.data.data
                .sort((a, b) => a.label.localeCompare(b.label))
                .sort((a, b) => a.count - b.count)
                .reverse()
                .map((d, i) => {
                metricsData.push({ label: d.label, count: d.count, color: metricColors[i] });
            });
            setAudienceStatusData(metricsData);
            setAudienceStatusCachedAt(response.data.cached_at);
            setAudienceStatusLoading(false);
        })
            .catch(error => {
            setAudienceStatusError(true);
            if (!api.isCancel(error)) {
                console.error(error);
                setAudienceStatusLoading(false);
            }
        });
        return () => api.cancel();
    };
    const getAudienceTrend = invalidateCache => {
        setAudienceTrendLoading(true);
        if (audienceTrendError)
            setAudienceTrendError(false);
        let trendsLabels = getTrendLabels(audienceTrendRange);
        let labels = getLabels(trendsLabels, audienceTrendRange);
        setAudienceTrendLabels(labels);
        let interval = audienceTrendRange;
        let startDate;
        let endDate;
        if (audienceTrendRange === "last_7_days") {
            interval = "custom";
            startDate = format(subDays(new Date(), 6), "yyyy-MM-dd") + " 00:00:00";
            endDate = format(new Date(), "yyyy-MM-dd") + " 23:59:59";
        }
        else if (audienceTrendRange === "last_30_days") {
            interval = "custom";
            startDate = format(subDays(new Date(), 29), "yyyy-MM-dd") + " 00:00:00";
            endDate = format(new Date(), "yyyy-MM-dd") + " 23:59:59";
        }
        api.GET_metrics("trend", "leads", interval, undefined, undefined, "audience_id", audience_id, undefined, invalidateCache ? 1 : undefined, startDate, endDate)
            .then(response => {
            let values = [];
            trendsLabels.map(label => {
                if (response.data.data.find(a => a.date === label)) {
                    values.push(response.data.data.find(a => a.date === label).count);
                }
                else {
                    values.push(0);
                }
            });
            setAudienceTrendCachedAt(response.data.cached_at);
            setAudienceTrendValues(values);
            setAudienceTrendLoading(false);
        })
            .catch(error => {
            setAudienceTrendError(true);
            if (!api.isCancel(error)) {
                console.error(error);
                setAudienceTrendLoading(false);
            }
        });
        return () => api.cancel();
    };
    useEffect(() => {
        if (audienceValueRange !== "custom") {
            setAudienceValueStartDate("");
            setAudienceValueEndDate("");
            setAudienceValueStartTime("");
            setAudienceValueEndTime("");
            getAudienceValue(undefined);
        }
    }, [audienceValueRange]);
    useEffect(() => {
        if (isChosenValueCustomRange &&
            audienceValueStartDate !== "" &&
            audienceValueEndDate !== "") {
            getAudienceValue(undefined);
        }
    }, [isChosenValueCustomRange]);
    useEffect(() => {
        if (audienceStatusRange !== "custom") {
            setAudienceStatusStartDate("");
            setAudienceStatusEndDate("");
            setAudienceStatusStartTime("");
            setAudienceStatusEndTime("");
            getAudienceStatusValues(undefined);
        }
    }, [audienceStatusRange]);
    useEffect(() => {
        if (isChosenStatusCustomRange &&
            audienceStatusStartDate !== "" &&
            audienceStatusEndDate !== "") {
            getAudienceStatusValues(undefined);
        }
    }, [isChosenStatusCustomRange]);
    useEffect(() => {
        getAudienceTrend(undefined);
    }, [audienceTrendRange]);
    useEffect(() => {
        if (audienceValueMetricsRefresh || refreshMetrics) {
            getAudienceValue(true);
            setAudienceValueMetricsRefresh(false);
        }
        if (audienceTrendMetricsRefresh || refreshMetrics) {
            getAudienceTrend(true);
            setAudienceTrendMetricsRefresh(false);
        }
        if (audienceStatusMetricsRefresh || refreshMetrics) {
            getAudienceStatusValues(true);
            setAudienceStatusMetricsRefresh(false);
        }
    }, [
        audienceValueMetricsRefresh,
        audienceTrendMetricsRefresh,
        audienceStatusMetricsRefresh,
        refreshMetrics,
    ]);
    return (_jsx(Box, Object.assign({ sx: {
            marginBottom: 4,
        } }, { children: _jsxs(Grid, Object.assign({ container: true, spacing: 4 }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12, md: 6, xl: 4 }, { children: _jsx(MetricValue, { title: t("campaigns.audiences.leads.leads"), value: audienceValue, range: audienceValueRange, setRange: setAudienceValuesRange, isLoading: audienceValueLoading, setIsLoading: setAudienceValueLoading, cachedAt: audienceValueCachedAt, refresh: audienceValueMetricsRefresh, setRefresh: setAudienceValueMetricsRefresh, startDate: audienceValueStartDate, setStartDate: setAudienceValueStartDate, endDate: audienceValueEndDate, setEndDate: setAudienceValueEndDate, startTime: audienceValueStartTime, setStartTime: setAudienceValueStartTime, endTime: audienceValueEndTime, setEndTime: setAudienceValueEndTime, setIsChosenCustomRange: setIsChosenValueCustomRange, isError: audienceValueError }) })), _jsx(Grid, Object.assign({ item: true, xs: 12, md: 6, xl: 4 }, { children: _jsx(MetricTrend, { title: t("campaigns.audiences.leads.metrics.leads-trend"), label: t("campaigns.audiences.leads.leads"), labels: audienceTrendLabels, values: audienceTrendValues, range: audienceTrendRange, setRange: setAudienceTrendRange, isLoading: audienceTrendLoading, setIsLoading: setAudienceTrendLoading, cachedAt: audienceTrendCachedAt, refresh: audienceTrendMetricsRefresh, setRefresh: setAudienceTrendMetricsRefresh, isError: audienceTrendError }) })), _jsx(Grid, Object.assign({ item: true, xs: 12, md: 6, xl: 4 }, { children: _jsx(MetricPartition, { title: t("campaigns.audiences.leads.metrics.leads-status"), metricData: audienceStatusData, range: audienceStatusRange, setRange: setAudienceStatusRange, isLoading: audienceStatusLoading, setIsLoading: setAudienceStatusLoading, cachedAt: audienceStatusCachedAt, refresh: audienceStatusMetricsRefresh, setRefresh: setAudienceStatusMetricsRefresh, startDate: audienceStatusStartDate, setStartDate: setAudienceStatusStartDate, endDate: audienceStatusEndDate, setEndDate: setAudienceStatusEndDate, startTime: audienceStatusStartTime, setStartTime: setAudienceStatusStartTime, endTime: audienceStatusEndTime, setEndTime: setAudienceStatusEndTime, setIsChosenCustomRange: setIsChosenStatusCustomRange, metricType: "audience", isError: audienceStatusError }) }))] })) })));
}
